import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { memberAPI } from '../api/memberAPI';
import { regionAPI } from '../api/regionAPI';
import { parseCSV } from '../utils/csv';
import { formatDate, formatPhone, isMinor } from '../utils/format';
import { downloadVCard } from '../utils/vcard';
import { Avatar } from '../components/Avatar';
import { SearchInput } from '../components/SearchInput';
import { FilterPopover } from '../components/FilterPopover';
import { FilterBar } from '../components/FilterBar';
import { useClickOutside } from '../hooks/useClickOutside';

export function Members() {
    const { isAuthenticated, isAdmin, user } = useAuth();
    const navigate = useNavigate();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [q, setQ] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 20 });
    const [regions, setRegions] = useState([]);

    // CSV import state
    const [showImport, setShowImport] = useState(false);
    const [csvRows, setCsvRows] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importColumns, setImportColumns] = useState(new Set());
    const fileInputRef = useRef(null);

    // Sort state
    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    const isRegionalLeader = !isAdmin && user?.roles?.includes('regional_leader');
    const isLeaderRole = !isAdmin && (user?.roles?.includes('regional_leader') || user?.roles?.includes('regional_assistant'));

    // Column visibility
    const allColumns = [
        { key: 'name', label: 'Name', default: true },
        ...(!isLeaderRole ? [{ key: 'status', label: 'Status', default: true }] : []),
        { key: 'email', label: 'Email', default: true },
        { key: 'phone', label: 'Phone', default: true },
        ...(!isLeaderRole ? [{ key: 'region', label: 'Region', default: true }] : []),
        { key: 'address', label: 'Address', default: false },
        { key: 'birthdate', label: 'Birthdate', default: false },
        { key: 'wedding_anniversary', label: 'Anniversary', default: false },
    ];
    const [visibleCols, setVisibleCols] = useState(() => {
        try {
            const saved = localStorage.getItem('members_columns');
            if (saved) return JSON.parse(saved);
        } catch {}
        return allColumns.filter((c) => c.default).map((c) => c.key);
    });
    const [showColPicker, setShowColPicker] = useState(false);
    const colPickerRef = useRef(null);

    useClickOutside(colPickerRef, useCallback(() => setShowColPicker(false), []));

    const toggleColumn = (key) => {
        setVisibleCols((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            localStorage.setItem('members_columns', JSON.stringify(next));
            return next;
        });
    };

    const activeFilterCount = (regionFilter ? 1 : 0) + (statusFilter ? 1 : 0);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => { setQ(searchInput); setPage(1); }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
        setPage(1);
    };

    const regionName = (id) => {
        const r = regions.find((r) => r.id === id);
        return r ? r.name : id || '-';
    };

    const renderCell = (m, key) => {
        const minor = m.is_minor || isMinor(m.birthdate);
        switch (key) {
            case 'name': return <Link to={`/members/${m.id}`} className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-600"><Avatar name={m.name} photoUrl={m.photo_url} size="sm" />{m.name || '-'}</Link>;
            case 'status': {
                const s = m.status || 'active';
                return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${s === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>{s}</span>;
            }
            case 'email': return minor ? <span className="text-xs text-slate-400 italic">Minor</span> : m.email ? <a href={`mailto:${m.email}`} className="text-accent-500 hover:text-accent-600">{m.email}</a> : '-';
            case 'phone': return minor ? <span className="text-xs text-slate-400 italic">Minor</span> : m.phone ? <a href={`tel:${m.phone}`} className="text-accent-500 hover:text-accent-600">{formatPhone(m.phone)}</a> : '-';
            case 'region': return regionName(m.region_id);
            case 'address': return minor ? <span className="text-xs text-slate-400 italic">Minor</span> : m.address || '-';
            case 'birthdate': return formatDate(m.birthdate);
            case 'wedding_anniversary': return formatDate(m.wedding_anniversary);
            default: return '-';
        }
    };

    // Fetch regions on mount
    useEffect(() => {
        if (!isAuthenticated) return;
        regionAPI.getRegions().then((res) => {
            const payload = res.data.data || {};
            setRegions(payload.items || []);
        }).catch(() => {});
    }, [isAuthenticated]);

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, per_page: perPage };
            if (q) params.q = q;
            if (regionFilter) params.region_id = regionFilter;
            if (statusFilter) params.status = statusFilter;
            if (sortField) { params.sort_by = sortField; params.sort_dir = sortDir; }
            const res = await memberAPI.getMembers(params);
            const payload = res.data.data || {};
            setMembers(payload.items || []);
            setMeta(payload.meta || { total: 0, page, per_page: perPage });
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load members');
        } finally {
            setLoading(false);
        }
    }, [page, perPage, q, regionFilter, statusFilter, sortField, sortDir]);

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchMembers();
    }, [isAuthenticated, fetchMembers]);

    // CSV import handlers
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const rows = parseCSV(evt.target.result);
            setCsvRows(rows);
            setImportResult(null);
            if (rows.length > 0) {
                const knownCols = ['name', 'first_name', 'last_name', 'middle_initial', 'email', 'phone', 'address', 'birthdate', 'wedding_anniversary', 'region'];
                const detected = knownCols.filter((col) => Object.keys(rows[0]).some((k) => k === col || k.replace(/ /g, '_') === col || k.replace(/_/g, ' ') === col));
                setImportColumns(new Set(detected));
            }
        };
        reader.readAsText(file);
    };

    const resolveField = (row, ...keys) => {
        for (const k of keys) {
            const v = row[k] || row[k.replace(/ /g, '_')] || row[k.replace(/_/g, ' ')];
            if (v) return v;
        }
        return '';
    };

    const handleImport = async () => {
        if (csvRows.length === 0) return;
        setImporting(true);
        setImportResult(null);
        setError(null);
        try {
            const mapped = csvRows.map((row) => ({
                name: row.name || '',
                first_name: row.first_name || row['first name'] || '',
                last_name: row.last_name || row['last name'] || '',
                middle_initial: row.middle_initial || row['middle initial'] || row['mi'] || '',
                email: importColumns.has('email') ? (row.email || '') : '',
                phone: importColumns.has('phone') ? (row.phone || '') : '',
                address: importColumns.has('address') ? (row.address || '') : '',
                birthdate: importColumns.has('birthdate') ? resolveField(row, 'birthdate', 'birthday', 'birth date', 'dob', 'date of birth', 'birth_date') : '',
                wedding_anniversary: importColumns.has('wedding_anniversary') ? resolveField(row, 'wedding_anniversary', 'wedding anniversary', 'anniversary', 'wedding_date', 'wedding date') : '',
                region: importColumns.has('region') ? (row.region || '') : '',
            }));
            const res = await memberAPI.importMembers({ members: mapped });
            setImportResult(res.data);
            if (res.data.imported > 0) {
                setSuccess(`Imported ${res.data.imported} member(s).${res.data.failed > 0 ? ` ${res.data.failed} failed.` : ''}`);
                fetchMembers();
            } else if (res.data.failed > 0) {
                setError(`All ${res.data.failed} row(s) failed. See details below.`);
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Import failed';
            setError(`Import request failed: ${msg}`);
        } finally {
            setImporting(false);
        }
    };

    const handleCloseImport = () => {
        setShowImport(false);
        setCsvRows([]);
        setImportResult(null);
        setImportColumns(new Set());
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    if (!isAuthenticated) {
        return (
            <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
                <Card>
                    <CardBody>
                        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Not authenticated</h2>
                        <p className="text-sm text-slate-500 mt-1">Please login to manage members.</p>
                        <Button onClick={() => navigate('/login')}>Login</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Members</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Manage members grouped by region</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={() => navigate('/dashboard')}>Back</Button>
                    {(isAdmin || user?.roles?.includes('regional_leader') || user?.roles?.includes('intake')) && (
                        <Button variant="secondary" onClick={() => { setShowImport(!showImport); if (showImport) handleCloseImport(); }}>
                            {showImport ? 'Cancel Import' : 'Import CSV'}
                        </Button>
                    )}
                    {(isAdmin || user?.roles?.includes('regional_leader') || user?.roles?.includes('intake')) && (
                        <Button onClick={() => navigate('/members/new')}>+ Add Member</Button>
                    )}
                </div>
            </header>

            <main>
                {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
                {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

                {showImport && (
                    <Card className="mb-4">
                        <CardHeader>
                            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Import Members from CSV</h2>
                        </CardHeader>
                        <CardBody>
                            <p className="text-sm text-slate-500 mb-3">
                                CSV format: <code className="text-xs bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded">first_name,last_name,middle_initial,email,phone,address,birthdate,wedding_anniversary,region</code><br />
                                <strong>First name</strong> and <strong>last name</strong> are required. A single <strong>name</strong> column also works. Region matches by name.
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileSelect}
                                className="mb-3 text-sm text-slate-700 dark:text-slate-300"
                            />
                            {csvRows.length > 0 && (
                                <>
                                    <div className="flex flex-wrap gap-x-4 gap-y-2 items-center mb-3">
                                        <span className="font-semibold text-sm text-slate-700 dark:text-slate-300">Columns to import:</span>
                                        {[
                                            { key: 'name', label: 'Name' },
                                            { key: 'email', label: 'Email' },
                                            { key: 'phone', label: 'Phone' },
                                            { key: 'region', label: 'Region' },
                                            { key: 'address', label: 'Address' },
                                            { key: 'birthdate', label: 'Birthdate' },
                                            { key: 'wedding_anniversary', label: 'Anniversary' },
                                        ].filter((col) => importColumns.has(col.key) || col.key === 'name').map((col) => (
                                            <label key={col.key} className={`flex items-center gap-1 text-sm ${col.key === 'name' ? 'cursor-default' : 'cursor-pointer'} text-slate-700 dark:text-slate-300`}>
                                                <input
                                                    type="checkbox"
                                                    checked={importColumns.has(col.key)}
                                                    disabled={col.key === 'name'}
                                                    onChange={() => {
                                                        setImportColumns((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(col.key)) next.delete(col.key);
                                                            else next.add(col.key);
                                                            return next;
                                                        });
                                                    }}
                                                />
                                                {col.label}
                                            </label>
                                        ))}
                                    </div>
                                    <div className="overflow-auto max-h-[300px] mb-3">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="bg-slate-50 dark:bg-slate-800/50">
                                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">#</th>
                                                    {importColumns.has('name') && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Name</th>}
                                                    {importColumns.has('email') && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Email</th>}
                                                    {importColumns.has('phone') && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Phone</th>}
                                                    {importColumns.has('region') && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Region</th>}
                                                    {importColumns.has('address') && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Address</th>}
                                                    {importColumns.has('birthdate') && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Birthdate</th>}
                                                    {importColumns.has('wedding_anniversary') && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Anniversary</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {csvRows.map((row, i) => (
                                                    <tr key={i} className="even:bg-slate-50/50 dark:even:bg-slate-800/30">
                                                        <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{i + 1}</td>
                                                        {importColumns.has('name') && <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{row.name || '-'}</td>}
                                                        {importColumns.has('email') && <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{row.email || '-'}</td>}
                                                        {importColumns.has('phone') && <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{row.phone || '-'}</td>}
                                                        {importColumns.has('region') && <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{row.region || '-'}</td>}
                                                        {importColumns.has('address') && <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{row.address || '-'}</td>}
                                                        {importColumns.has('birthdate') && <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{resolveField(row, 'birthdate', 'birthday', 'birth date', 'dob', 'date of birth', 'birth_date') || '-'}</td>}
                                                        {importColumns.has('wedding_anniversary') && <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">{resolveField(row, 'wedding_anniversary', 'wedding anniversary', 'anniversary', 'wedding_date', 'wedding date') || '-'}</td>}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button onClick={handleImport} disabled={importing}>
                                            {importing ? 'Importing...' : `Import ${csvRows.length} Row(s)`}
                                        </Button>
                                        <Button variant="secondary" onClick={handleCloseImport}>Cancel</Button>
                                    </div>
                                </>
                            )}
                            {importResult && (
                                <div className={`mt-3 p-3 rounded-md border ${importResult.failed > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'}`}>
                                    <p className="mb-2 font-semibold text-sm text-slate-800 dark:text-slate-200">
                                        Result: {importResult.imported} imported, {importResult.failed} failed
                                    </p>
                                    {importResult.errors && importResult.errors.length > 0 && (
                                        <div className="max-h-[200px] overflow-auto">
                                            <ul className="m-0 pl-5 text-sm text-red-800 dark:text-red-400">
                                                {importResult.errors.map((e, i) => (
                                                    <li key={i} className="mb-0.5">Row {e.row}: {e.error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}

                <FilterBar
                    search={
                        <SearchInput
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search name or email"
                        />
                    }
                    filters={<>
                        {!isRegionalLeader && (
                            <FilterPopover
                                label="Region"
                                value={regionFilter}
                                onChange={(v) => { setRegionFilter(v); setPage(1); }}
                                options={[
                                    { value: '__none', label: 'Unassigned' },
                                    ...regions.map((r) => ({ value: r.id, label: r.name })),
                                ]}
                                allLabel="All Regions"
                            />
                        )}
                        {!isLeaderRole && (
                            <FilterPopover
                                label="Status"
                                value={statusFilter}
                                onChange={(v) => { setStatusFilter(v); setPage(1); }}
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'inactive', label: 'Inactive' },
                                ]}
                                allLabel="All Statuses"
                            />
                        )}
                    </>}
                    actions={<>
                        <FilterPopover
                            label="Per page"
                            value={String(perPage)}
                            onChange={(v) => { setPerPage(Number(v || 20)); setPage(1); }}
                            options={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                            ]}
                            includeAllOption={false}
                        />
                        <div className="relative" ref={colPickerRef}>
                            <Button size="sm" variant="secondary" onClick={() => setShowColPicker(!showColPicker)}>
                                Columns
                            </Button>
                            {showColPicker && (
                                <div className="absolute right-0 top-full mt-1 z-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-2 min-w-[160px]">
                                    {allColumns.map((col) => (
                                        <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer whitespace-nowrap text-slate-700 dark:text-slate-300">
                                            <input
                                                type="checkbox"
                                                checked={visibleCols.includes(col.key)}
                                                onChange={() => toggleColumn(col.key)}
                                            />
                                            {col.label}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>}
                    activeFilterCount={activeFilterCount}
                    onClearFilters={() => { setRegionFilter(''); setStatusFilter(''); setPage(1); }}
                />

                <Card>
                    <CardHeader>
                        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Members ({meta?.total ?? members.length})</h2>
                    </CardHeader>
                    <CardBody>
                        {loading ? (
                            <p className="text-center text-slate-500 py-6">Loading...</p>
                        ) : members.length === 0 ? (
                            <p className="text-center text-slate-500 py-6">No members found</p>
                        ) : (
                            <>
                                {/* Desktop table */}
                                <div className="hidden sm:block overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-50 dark:bg-slate-800/50">
                                                {allColumns.filter((c) => visibleCols.includes(c.key)).map((col) => (
                                                    <th
                                                        key={col.key}
                                                        className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200"
                                                        onClick={() => handleSort(col.key)}
                                                    >
                                                        {col.label}
                                                        <span className="text-[0.7rem] text-slate-400 dark:text-slate-500">
                                                            {sortField === col.key ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                                                        </span>
                                                    </th>
                                                ))}
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {members.map((m) => (
                                                <tr key={m.id} className="even:bg-slate-50/50 dark:even:bg-slate-800/30">
                                                    {allColumns.filter((c) => visibleCols.includes(c.key)).map((col) => (
                                                        <td key={col.key} className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50 align-top">{renderCell(m, col.key)}</td>
                                                    ))}
                                                    <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50 align-top">
                                                        <div className="flex gap-1 items-center">
                                                            {!(m.is_minor || isMinor(m.birthdate)) && <Button size="sm" variant="secondary" onClick={() => downloadVCard(m)}>Import Contact</Button>}
                                                            {(isAdmin || user?.roles?.includes('regional_leader') || user?.roles?.includes('intake')) && (
                                                                <Button size="sm" variant="secondary" onClick={() => navigate(`/members/${m.id}`)}>Edit</Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Mobile cards */}
                                <div className="sm:hidden space-y-3">
                                    {members.map((m) => (
                                        <div key={m.id} className="card-base p-4 space-y-2">
                                            <div className="font-semibold text-slate-800 dark:text-slate-100">
                                                <Link to={`/members/${m.id}`} className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-600">
                                                    <Avatar name={m.name} photoUrl={m.photo_url} size="sm" />
                                                    {m.name || '-'}
                                                </Link>
                                            </div>
                                            {allColumns.filter((c) => visibleCols.includes(c.key) && c.key !== 'name').map((col) => (
                                                <div key={col.key} className="flex justify-between text-sm">
                                                    <span className="font-medium text-slate-500 dark:text-slate-400">{col.label}</span>
                                                    <span className="text-slate-700 dark:text-slate-300">{renderCell(m, col.key)}</span>
                                                </div>
                                            ))}
                                            <div className="flex flex-wrap gap-2 pt-2">
                                                {!(m.is_minor || isMinor(m.birthdate)) && <Button size="sm" variant="secondary" onClick={() => downloadVCard(m)}>Import Contact</Button>}
                                                {(isAdmin || user?.roles?.includes('regional_leader') || user?.roles?.includes('intake')) && (
                                                    <Button size="sm" variant="secondary" onClick={() => navigate(`/members/${m.id}`)}>Edit</Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </CardBody>
                    {meta && meta.total > 0 && (() => {
                        const totalPages = Math.ceil(meta.total / meta.per_page);
                        const start = (page - 1) * meta.per_page + 1;
                        const end = Math.min(page * meta.per_page, meta.total);
                        const pages = [];
                        const add = (n) => { if (n >= 1 && n <= totalPages && !pages.includes(n)) pages.push(n); };
                        add(1);
                        add(page - 1);
                        add(page);
                        add(page + 1);
                        add(totalPages);
                        pages.sort((a, b) => a - b);
                        const withEllipsis = [];
                        pages.forEach((p, i) => {
                            if (i > 0 && p - pages[i - 1] > 1) withEllipsis.push('...');
                            withEllipsis.push(p);
                        });
                        return (
                            <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-200 dark:border-slate-700">
                                <Button size="sm" variant="secondary" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>
                                    Prev
                                </Button>
                                <div className="flex items-center gap-1">
                                    {withEllipsis.map((p, i) =>
                                        p === '...' ? (
                                            <span key={`e${i}`} className="px-1 text-slate-400">&hellip;</span>
                                        ) : (
                                            <button
                                                key={p}
                                                className={`px-2.5 py-1 text-sm rounded ${p === page ? 'bg-accent-500 text-white font-semibold' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                                onClick={() => setPage(p)}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}
                                </div>
                                <span className="text-xs text-slate-500 hidden sm:inline">Showing {start}&ndash;{end} of {meta.total}</span>
                                <Button size="sm" variant="secondary" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                                    Next
                                </Button>
                            </div>
                        );
                    })()}
                </Card>
            </main>
        </div>
    );
}

export default Members;
